import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Users, ArrowLeft, Save, Send, RotateCcw, History } from "lucide-react";
import { Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import {
    APPRAISALS,
    APPRAISAL_RM_REVIEW,
} from "../../constants/api";

import StatusPill from "../../components/appraisal/StatusPill";
import useAppraisalRole from "../../hooks/useAppraisalRole";
import KPIGoalRow from "../../components/appraisal/KPIGoalRow";
import AuditTimeline from "../../components/appraisal/AuditTimeline";
import GoalRevisePanel from "../../components/appraisal/GoalRevisePanel";
import CheckInPanel from "../../components/appraisal/CheckInPanel";
import CompetencyPanel from "../../components/appraisal/CompetencyPanel";
import DevelopmentPlanPanel from "../../components/appraisal/DevelopmentPlanPanel";
import ReviewPanel from "../../components/appraisal/ReviewPanel";
import FeedbackSummary from "../../components/appraisal/FeedbackSummary";
import SendBackModal from "../../components/appraisal/SendBackModal";

import { brandHeadingText, brandMutedText, brandPanelBorder, brandPanelGradient } from "../../components/ui/statusStyles";
import { cn } from "../../utils/tailwindUtils";
import { StatusGuidance } from "../../components/appraisal/AppraisalPieces";
const reviewableStatuses = new Set(["submitted_to_rm", "sent_back_by_hr"]);


/* The reason an appraisal came back. A send-back always carries one -- the
   endpoint refuses without it -- but it lived only in the audit trail, so the
   person who had to act on it never saw it unless they opened History. */
const SentBackNotice = ({ sentBack }) => {
    if (!sentBack?.reason) return null;
    return (
        <div className="rounded-md border border-orange-200 dark:border-orange-900/40 bg-orange-50 dark:bg-orange-900/20 p-3 sm:p-4">
            <p className="text-sm font-medium text-orange-900 dark:text-orange-200">
                Sent back by {sentBack.by?.name || sentBack.by_role}
            </p>
            <p className="text-sm text-orange-800 dark:text-orange-200 mt-1">
                &ldquo;{sentBack.reason}&rdquo;
            </p>
            <p className="text-xs text-orange-700 dark:text-orange-300 mt-2">
                Make the changes below, then submit again.
            </p>
        </div>
    );
};

const TeamReviewDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { fetch, update, create } = useApiMethods();
    const { isRM } = useAppraisalRole();

    const [appraisal, setAppraisal] = useState(null);
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmApprove, setConfirmApprove] = useState(false);
    // Feeds the nine-box. Left unset unless the manager picks one: the grid
    // reports "not assessed" separately rather than plotting it as Low.
    const [potential, setPotential] = useState(null);
    const [sendBackOpen, setSendBackOpen] = useState(false);
    const [showHistory, setShowHistory] = useState(true);
    const [historyKey, setHistoryKey] = useState(0);

    const loadAppraisal = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(APPRAISALS, { id });
            setAppraisal(res?.data || null);
            setGoals(res?.data?.goals || []);
            setPotential(res?.data?.potential_rating ?? null);
        } catch (_) {
            setAppraisal(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => { loadAppraisal(); }, [loadAppraisal]);

    if (loading) {
        return (
            <ContainerCard scrollable>
                <div className="text-center py-12 text-muted-foreground">Loading…</div>
            </ContainerCard>
        );
    }

    if (!appraisal) {
        return (
            <ContainerCard scrollable>
                <ModuleTitleCard Icon={Users} title="Appraisal Not Found" />
                <div className="px-3">
                    <Button variant="outline" size="sm" onClick={() => navigate("/appraisal/team-review")}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                </div>
            </ContainerCard>
        );
    }

    const canReview = isRM && reviewableStatuses.has(appraisal.status) && !appraisal.is_locked;
    // Potential outlives the review itself: it feeds the nine-box, not the
    // score, so it stays editable until the appraisal is published.
    const canSetPotential = isRM && !appraisal.published_at && !appraisal.is_locked;
    const unratedGoals = goals.filter((g) => g.rm_rating === null || g.rm_rating === undefined);
    const allGoalsRated = unratedGoals.length === 0;

    const handleGoalChange = (updated) => {
        setGoals((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
    };

    const buildPayload = () => ({
        id,
        goals: goals.map((g) => ({
            id: g.id,
            rm_rating: g.rm_rating,
            rm_comment: g.rm_comment,
        })),
    });

    const saveDraft = async () => {
        setSaving(true);
        try {
            const res = await update(APPRAISALS, buildPayload());
            if (res?.status === "success") {
                ToastMsg("success", "Review draft saved");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
            } else {
                ToastMsg("error", res?.message || "Failed to save");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const savePotential = async () => {
        setSaving(true);
        try {
            const res = await update(APPRAISALS, { id, potential_rating: potential });
            if (res?.status === "success") {
                ToastMsg("success", "Potential saved");
                if (res.data) setAppraisal(res.data);
            } else {
                ToastMsg("error", res?.message || "Could not save the potential rating");
            }
        } catch (_) {
            ToastMsg("error", "Could not save the potential rating");
        } finally {
            setSaving(false);
        }
    };

    const approve = async () => {
        if (!allGoalsRated) {
            ToastMsg("error", "Please rate all KPIs before approving");
            setConfirmApprove(false);
            return;
        }
        try {
            const res = await create(APPRAISAL_RM_REVIEW, {
                action: "approve",
                comment: "RM approved and forwarded to HR.",
                potential_rating: potential,
                ...buildPayload(),
            });
            if (res?.status === "success") {
                ToastMsg("success", "Approved and sent to HR");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
                setHistoryKey((k) => k + 1);
            } else {
                ToastMsg("error", res?.message || "Failed to approve");
            }
        } catch (_) {
            ToastMsg("error", "Failed to approve");
        }
        setConfirmApprove(false);
    };

    const sendBack = async (comment) => {
        try {
            const res = await create(APPRAISAL_RM_REVIEW, {
                action: "send_back",
                comment,
                ...buildPayload(),
            });
            if (res?.status === "success") {
                ToastMsg("success", "Sent back to employee");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
                setHistoryKey((k) => k + 1);
            } else {
                ToastMsg("error", res?.message || "Failed to send back");
            }
        } catch (_) {
            ToastMsg("error", "Failed to send back");
        }
        setSendBackOpen(false);
    };

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={Users}
                title={appraisal.employee?.name || "Team Review"}
                subtitle={`${appraisal.employee?.designation || ""} · ${appraisal.cycle_name || ""}`}
            />

            <div className="px-3 pb-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/team-review")} className="self-start">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                        <StatusPill value={appraisal.status} />
                        {appraisal.self_submitted_at && (
                            <span className="text-xs text-muted-foreground">
                                Submitted {dayjs(appraisal.self_submitted_at).format("DD-MM-YYYY")}
                            </span>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setShowHistory((v) => !v)}>
                            <History className="w-4 h-4 mr-1" /> History
                        </Button>
                    </div>
                </div>

                <StatusGuidance status={appraisal.status} role="rm" />

                <SentBackNotice sentBack={appraisal.sent_back} />

                <GoalRevisePanel
                    appraisal={appraisal}
                    goals={goals}
                    onUpdated={(data) => {
                        if (!data) return loadAppraisal();
                        setAppraisal(data);
                        setGoals(data.goals || []);
                        setHistoryKey((k) => k + 1);
                    }}
                />

                {appraisal.status === "pending_goal_agreement" && (
                    <div className={cn("rounded-md border p-3 sm:p-4", brandPanelBorder, brandPanelGradient)}>
                        <p className={cn("text-sm font-medium", brandHeadingText)}>
                            Waiting for {appraisal.employee?.name || "the employee"} to agree these goals
                        </p>
                        <p className={cn("text-xs mt-0.5", brandMutedText)}>
                            They can accept them or send them back with a reason. Self-evaluation
                            opens once they are agreed.
                        </p>
                    </div>
                )}

                {/* 360 is something you act on, so it does not sit behind
                    the History button -- that is for the audit trail. Hidden
                    entirely on a cycle that does not run 360. */}
                {appraisal.cycle_feedback_enabled && (
                    <>
                        <ReviewPanel appraisalId={appraisal.id} mode="approve"
                            readOnly={!!appraisal.published_at || appraisal.is_locked} />
                        <FeedbackSummary appraisalId={appraisal.id} />
                    </>
                )}

                {showHistory && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                        <h3 className="text-base font-semibold mb-3">Activity History</h3>
                        <AuditTimeline appraisalId={appraisal.id} refreshKey={historyKey} />
                    </div>
                )}

                <div className="space-y-3">
                    {goals.map((g) => (
                        <KPIGoalRow
                            key={g.id}
                            goal={g}
                            role="rm"
                            readOnly={!canReview}
                            onChange={handleGoalChange}
                        />
                    ))}
                </div>

                <CheckInPanel
                    appraisalId={appraisal.id}
                    goals={goals}
                    role="rm"
                    locked={appraisal.is_locked}
                />

                <CompetencyPanel
                    appraisal={appraisal}
                    role="rm"
                />

                <DevelopmentPlanPanel
                    appraisal={appraisal}
                    goals={goals}
                    role="rm"
                />

                {canSetPotential && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                        <h3 className="text-base font-semibold mb-1">Potential</h3>
                        <p className="text-xs text-muted-foreground mb-3">
                            Your view of how far this person could go, which is the second axis
                            of the nine-box. Separate from performance, optional, and not shown
                            to the employee. Leave it blank if you have not formed a view.
                        </p>
                        <Select
                            allowClear
                            className="w-full sm:w-72"
                            placeholder="Not assessed"
                            value={potential}
                            onChange={(v) => setPotential(v ?? null)}
                            options={[
                                { value: 1, label: "Low" },
                                { value: 2, label: "Medium" },
                                { value: 3, label: "High" },
                            ]}
                        />
                        {!canReview && (
                            <div className="mt-3">
                                <Button size="sm" variant="outline" disabled={saving}
                                    onClick={savePotential}>
                                    <Save className="w-4 h-4 mr-1" />
                                    {saving ? "Saving…" : "Save potential"}
                                </Button>
                                <p className="text-xs text-muted-foreground mt-2">
                                    This appraisal has moved on, so it saves on its own.
                                    It can still be changed until it is published.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {canReview && (
                    <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sticky bottom-0 bg-gradient-to-t from-white dark:from-gray-900 from-50% to-transparent pt-3 -mx-3 px-3 pb-1">
                        <Button variant="outline" onClick={saveDraft} disabled={saving}>
                            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save Draft"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setSendBackOpen(true)}
                            className="border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20"
                        >
                            <RotateCcw className="w-4 h-4 mr-1" /> Send Back
                        </Button>
                        {/* A disabled button fires no mouse events, so its title
                            attribute never surfaces. The reason has to be on the
                            page or it does not exist. */}
                        {!allGoalsRated && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 self-center sm:mr-2">
                                {unratedGoals.length === goals.length
                                    ? `Rate all ${goals.length} KPIs to forward this to HR`
                                    : `${unratedGoals.length} of ${goals.length} KPIs still need your rating`}
                            </p>
                        )}
                        <Button
                            variant="brand"
                            onClick={() => setConfirmApprove(true)}
                            disabled={!allGoalsRated}
                            title={!allGoalsRated ? "Rate all KPIs first" : undefined}
                        >
                            <Send className="w-4 h-4 mr-1" /> Approve & Forward to HR
                        </Button>
                    </div>
                )}
            </div>

            <ConfirmPopupModal
                open={confirmApprove}
                onClose={() => setConfirmApprove(false)}
                onConfirm={approve}
                title="Approve & Forward"
                description="Forward this appraisal to HR for final approval? Your ratings and comments will be locked."
                type="info"
                confirmLabel="Approve"
            />

            <SendBackModal
                open={sendBackOpen}
                onClose={() => setSendBackOpen(false)}
                onConfirm={sendBack}
                title="Send Back to Employee"
                actor="RM"
            />
        </ContainerCard>
    );
};

export default TeamReviewDetail;
